import { b2bPriceListService } from '@/services/b2b-price-list.service';
import type { OrderItem, FulfillmentMode } from '../types';
import { itemGrossAmount, recalcItem } from './orderItemCalc';

export type B2BSuggestPatch = Partial<
  Pick<
    OrderItem,
    | 'suggestedPrice'
    | 'listPrice'
    | 'priceTier'
    | 'priceTierName'
    | 'price'
    | 'discountPercent'
    | 'discountAmount'
    | 'vat'
    | 'vatOverride'
    | 'tierDiscountType'
    | 'tierDiscountValue'
  >
>;

/**
 * TCR-2126 — đổ CK mặc định của cấp giá (snapshot `tierDiscountType/Value`) vào dòng, áp TRÊN
 * `item.price` (Giá áp dụng): PERCENT → CK %, VND → CK ₫ mỗi SP (tiền CK đúng số chẵn, % chỉ là
 * quy đổi để hiển thị — payload gửi `discount_type: 'amount'` + tiền CK dòng). Cấp không cấu hình
 * CK → 0. NVKD đã tự sửa CK (`hasDiscountOverride`) → giữ CK của họ, chỉ tính lại tiền.
 */
export function applyTierDiscount(item: OrderItem, fulfillmentMode: FulfillmentMode): OrderItem {
  if (item.hasDiscountOverride) return recalcItem(item, fulfillmentMode);
  const value = Number(item.tierDiscountValue ?? 0);
  if (item.tierDiscountType === 'VND' && value > 0) {
    const isQuantityMode = item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
    const perUnit = Math.min(value, Math.max(item.price, 0));
    // TCR-2224 — chuẩn MISA: thành tiền = làm tròn (Đơn giá × SL), tiền CK làm tròn về đồng và
    // không vượt thành tiền ⇒ tiền dòng luôn là số nguyên.
    const gross = itemGrossAmount(item.price, item.quantity, isQuantityMode);
    const discountAmount = Math.min(
      Math.round(isQuantityMode ? perUnit * item.quantity : perUnit),
      gross,
    );
    return {
      ...item,
      discountType: 'amount',
      discountPercent: item.price > 0 ? Math.round((perUnit / item.price) * 100 * 100) / 100 : 0,
      discountAmount,
      totalAmount: gross - discountAmount,
    };
  }
  const percent = item.tierDiscountType === 'PERCENT' && value > 0 ? Math.min(value, 100) : 0;
  return recalcItem({ ...item, discountType: 'percent', discountPercent: percent }, fulfillmentMode);
}

function resolveRetailPrice(item: OrderItem): number {
  const retail = item.retailPrice ?? item.listPrice ?? item.price;
  return Number.isFinite(retail) && retail > 0 ? retail : 0;
}

/** Không CKTM → Giá gợi ý = giá bán lẻ. */
function retailSuggestFallback(item: OrderItem): B2BSuggestPatch {
  const retail = resolveRetailPrice(item);
  const patch: B2BSuggestPatch = {
    suggestedPrice: retail,
    listPrice: retail,
    priceTier: 'RETAIL',
    priceTierName: 'Giá bán lẻ',
    tierDiscountType: null,
    tierDiscountValue: null,
  };
  if (!item.hasPriceOverride) {
    patch.price = retail;
  }
  return patch;
}

/** Gọi suggest CKTM cho 1 dòng POS (B2B). Không có CKTM → fallback giá bán lẻ. */
export async function fetchPosB2BSuggestPatch(
  item: OrderItem,
  customerId?: string,
): Promise<B2BSuggestPatch> {
  if (!item.brandId) return retailSuggestFallback(item);
  try {
    const suggested = await b2bPriceListService.suggest({
      brand_id: item.brandId,
      variant_id: item.variantId,
      customer_id: customerId,
      quantity: item.quantity,
    });
    if (!suggested) return retailSuggestFallback(item);

    const tradeDiscount = Number(suggested.display?.trade_discount_amount ?? 0);
    const isCustomerOverride = suggested.override_source === 'CUSTOMER_OVERRIDE';
    const isRetailOnly =
      !isCustomerOverride
      && (suggested.tier_key === 'RETAIL' || tradeDiscount <= 0);

    const patch: B2BSuggestPatch = {
      suggestedPrice: suggested.unit_price,
      listPrice: suggested.unit_price,
      priceTier: suggested.tier_key,
      priceTierName: isCustomerOverride
        ? (suggested.tier_name || 'Giá đặc biệt')
        : isRetailOnly
          ? (suggested.tier_name || 'Giá bán lẻ')
          : suggested.tier_name,
      tierDiscountType: suggested.discount_type ?? null,
      tierDiscountValue: suggested.discount_type ? Number(suggested.discount_value ?? 0) : null,
    };
    if (!item.hasPriceOverride) {
      patch.price = suggested.unit_price;
    }
    // TCR-1364: VAT của bảng giá đặc biệt (B2B) phải được ưu tiên CAO NHẤT —
    // set thẳng vào `vatOverride` (kênh duy nhất client tác động VAT, xem
    // `manualOverrideRate` trong `VatResolverService.resolve()`, ưu tiên tuyệt
    // đối). CHỈ set khi thu ngân CHƯA từng tự tay sửa VAT dòng này
    // (`!item.hasVatOverride`) — nếu đã sửa tay thì đây là chỉnh sửa có chủ
    // đích, không được suggest ghi đè (mirror logic `hasPriceOverride` ở trên).
    // Bảng giá KHÔNG có VAT (`vat_included` không phải `true`, hoặc `vat_rate`
    // không phải số) → không đụng gì, giữ nguyên hành vi cũ (rơi về VAT mặc
    // định sản phẩm/tenant qua VatResolverService).
    if (!item.hasVatOverride && suggested.vat_included === true && typeof suggested.vat_rate === 'number') {
      patch.vat = suggested.vat_rate;
      patch.vatOverride = suggested.vat_rate;
    }
    return patch;
  } catch {
    return retailSuggestFallback(item);
  }
}

export async function applyB2BSuggestToOrderItems(
  items: OrderItem[],
  fulfillmentMode: FulfillmentMode,
  customerId?: string,
  // TCR-2129: chỉ khách kênh SI/GT/MT mới snapshot CK chính sách; ngoài phạm vi thì xoá.
  opts?: { trackPolicy?: boolean },
): Promise<OrderItem[]> {
  const patches = await Promise.all(
    items.map(async (item) => {
      // Dòng thuộc cụm combo mang giá BE dựng từ combo_definitions. Ghi đè bằng
      // giá gợi ý B2B ở đây sẽ làm màn hình một số, đơn ghi một số khác — BE
      // định giá lại dòng combo TRƯỚC nhánh B2B và không phát lỗi nào.
      if (item.comboGroupId) return { id: item.id, patch: null };
      const patch = await fetchPosB2BSuggestPatch(item, customerId);
      return { id: item.id, patch };
    }),
  );

  let changed = false;
  const next = items.map((item) => {
    const found = patches.find((p) => p.id === item.id);
    if (!found?.patch) return item;
    const tiered = applyTierDiscount({ ...item, ...found.patch }, fulfillmentMode);
    // TCR-2129: snapshot CK chính sách theo cấp giá vừa suggest (fallback bán lẻ → type/value null).
    const candidate: OrderItem = {
      ...tiered,
      policyDiscount: opts?.trackPolicy
        ? { type: tiered.tierDiscountType ?? null, value: tiered.tierDiscountValue ?? null }
        : undefined,
    };
    const same =
      item.suggestedPrice === candidate.suggestedPrice
      && item.listPrice === candidate.listPrice
      && item.priceTier === candidate.priceTier
      && item.priceTierName === candidate.priceTierName
      && item.price === candidate.price
      // TCR-2126: CK cấp giá (đổi SL nhảy cấp, hoặc bảng giá sửa CK mà giá không đổi).
      && (item.tierDiscountType ?? null) === (candidate.tierDiscountType ?? null)
      && (item.tierDiscountValue ?? null) === (candidate.tierDiscountValue ?? null)
      && item.discountType === candidate.discountType
      && item.discountPercent === candidate.discountPercent
      && item.discountAmount === candidate.discountAmount
      // TCR-2129: snapshot chính sách đổi (đổi khách/kênh/cấp) dù tiền không đổi.
      && JSON.stringify(item.policyDiscount ?? null) === JSON.stringify(candidate.policyDiscount ?? null)
      // TCR-1364: bảng giá có thể đổi vat_rate mà tier/giá không đổi (vd NVKD
      // sửa VAT của bảng giá đang ACTIVE) — phải so sánh riêng để không bỏ lỡ
      // cập nhật VAT khi mọi field còn lại trùng nhau. `vat` của candidate chỉ
      // khác khi suggest được phép set (thu ngân chưa `hasVatOverride`).
      && item.vat === candidate.vat;
    if (same) return item;
    changed = true;
    return candidate;
  });
  return changed ? next : items;
}

/**
 * TCR-1262: khách hàng B2B bị xoá khỏi đơn (hoặc đổi về khách cá nhân) — giá
 * do ENGINE B2B tự động gán vào `item.price` (xem `fetchPosB2BSuggestPatch`
 * dòng ~73-75, chỉ set `patch.price` khi `!item.hasPriceOverride`) phải trả
 * lại giá bán lẻ, nếu không dòng hàng "dính" giá ưu đãi/override riêng của
 * khách vừa bị xoá sang khách vãng lai hoặc khách B2B khác — sai tiền thu
 * thật của khách (xem mô tả bug trong ticket).
 *
 * Giá do THU NGÂN tự tay sửa (`item.hasPriceOverride === true`, xem
 * `OrderItemsTable.tsx` `onUpdate`) thì GIỮ NGUYÊN — đây là chỉnh sửa có chủ
 * đích của thu ngân, không liên quan gì tới bảng giá B2B, không được ghi đè.
 * `fulfillmentMode` cần để `recalcItem` tính lại `discountAmount`/`totalAmount`
 * đúng theo `price` mới — bỏ recalc thì tổng dòng/tổng đơn lệch với đơn giá
 * hiển thị.
 */
export function clearB2BSuggestFields(
  items: OrderItem[],
  fulfillmentMode: FulfillmentMode,
): OrderItem[] {
  if (!items.some((i) => i.suggestedPrice != null || i.priceTier || i.hasPriceOverride || i.policyDiscount)) {
    return items;
  }
  return items.map((item) => {
    // TCR-1364: chỉ reset vat/vatOverride khi CHÍNH B2B suggest là bên đã ghi
    // đè chúng (`vatOverride !== undefined` nhưng KHÔNG phải do thu ngân tự sửa
    // tay, tức `!hasVatOverride` — 2 điều kiện này chỉ đồng thời đúng khi
    // `fetchPosB2BSuggestPatch` là nơi set). Nếu KHÔNG guard thêm điều kiện
    // `vatOverride !== undefined`, những dòng hàng CHƯA TỪNG qua B2B suggest
    // (vatOverride vẫn undefined từ lúc tạo, `item.vat` đang đúng bằng VAT mặc
    // định sản phẩm) sẽ bị xoá sạch VAT về undefined một cách vô cớ mỗi khi
    // khách B2B bị gỡ khỏi đơn — mất VAT mặc định mà không có gì để phục hồi
    // (khác với `price`, vốn luôn phục hồi được về `retailPrice` đã snapshot).
    const clearVat = item.vatOverride !== undefined && !item.hasVatOverride;
    const cleared: OrderItem = {
      ...item,
      suggestedPrice: undefined,
      listPrice: undefined,
      priceTier: undefined,
      priceTierName: undefined,
      // TCR-2614: dòng tách giữ cờ — giá 0 là chủ đích, không phải giá do bảng giá B2B gán.
      hasPriceOverride: item.splitLine === true,
      tierDiscountType: undefined,
      tierDiscountValue: undefined,
      // TCR-2129: bỏ khách B2B → bỏ luôn snapshot chính sách.
      policyDiscount: undefined,
      ...(clearVat ? { vat: undefined, vatOverride: undefined } : {}),
      // TCR-2126: CK do bảng giá tự đổ (chưa bị NVKD sửa tay) đi cùng giá B2B — về 0.
      ...(item.tierDiscountType && !item.hasDiscountOverride
        ? { discountType: 'percent' as const, discountPercent: 0, discountAmount: 0 }
        : {}),
    };
    if (item.hasPriceOverride) {
      // Giá do thu ngân tự sửa — không đụng vào (chỉ tính lại tiền nếu CK vừa về 0).
      return cleared.discountPercent === item.discountPercent ? cleared : recalcItem(cleared, fulfillmentMode);
    }
    const retail = resolveRetailPrice(item);
    if (item.price === retail && cleared.discountPercent === item.discountPercent) return cleared;
    return recalcItem({ ...cleared, price: retail }, fulfillmentMode);
  });
}
